"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { css } from "@/../styled-system/css";
import LoaderCircle from "@/assets/svg/loader-circle.svg";
import Input from "@/components/ui/Input";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  ReactForm,
} from "@/components/ui/ReactForm";
import { TNewsLetterForm, newsLetterForm } from "@/types/NewsLetterForm/types";

import Button from "../ui/Button";
import { toast } from "../ui/UseToast";

const newsletterFormCSS = {
  width: "100%",
  sm: {
    width: "fit",
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: "4",
  },
};

const newsletterInputCSS = {
  width: "100%",
  py: "3",
  rounded: "lg",
  color: "brandDarkGray",
  border: "none",
  backgroundColor: "white",
  fontSize: "md",
  mb: "4",
  ringColor: "white",
  ringWidth: "2px",
  fontFamily: "inter",
  sm: {
    mb: "unset",
    width: "320px",
  },
};

export default function NewsLetterSubscribeFormNoAction() {
  const form = useForm<TNewsLetterForm>({
    resolver: zodResolver(newsLetterForm),
    defaultValues: {
      email: "",
    },
  });

  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  const onSubmitForm = async (data: TNewsLetterForm) => {
    const response = await fetch("/api/newsletter", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    const responseData = await response.json();

    if (!responseData.ok) {
      toast({
        title: "Success",
        description: "Message sent successfully",
      });
      reset();
    } else {
      toast({
        title: "Error",
        description: "Message failed to send",
        variant: "destructive",
      });
    }
  };

  return (
    <ReactForm {...form}>
      <form
        className={css(newsletterFormCSS)}
        onSubmit={handleSubmit(onSubmitForm)}
      >
        <FormField
          control={control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  disabled={isSubmitting}
                  css={newsletterInputCSS}
                  placeholder="Enter your email"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          disabled={isSubmitting}
          type="submit"
          width="auto"
          variant="primary"
          css={{
            fontFamily: "inter",
            boxShadow: "none",
            _hover: {
              backgroundColor: "brandTeal/70",
            },
          }}
        >
          {isSubmitting && (
            <LoaderCircle width={20} className={css({ animation: "spin" })} />
          )}
          <span>Subscribe</span>
        </Button>
      </form>
    </ReactForm>
  );
}
